import * as React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  Chip,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';

import styles from './AgeRangesEditor.module.css';

export interface AgeRangeValue {
  min: number;
  max: number;
  /** Share of the panel in [0, 1]. */
  fraction: number;
}

export interface AgeRangesEditorProps {
  ranges: AgeRangeValue[];
  onChange: (ranges: AgeRangeValue[]) => void;
  bounds: { min: number; max: number };
  maxRanges: number;
  /** Errors keyed by row index. */
  rowErrors?: Record<number, string>;
  /** Error about the set as a whole, e.g. weights not adding up. */
  error?: string;
  disabled?: boolean;
}

const round = (n: number, places: number) => {
  const f = 10 ** places;
  return Math.round(n * f) / f;
};

/** Even weights whose rounded values still sum to exactly 1. */
export const evenFractions = (count: number): number[] => {
  if (count === 0) return [];
  const share = round(1 / count, 4);
  const fractions = Array.from({ length: count }, () => share);
  fractions[count - 1] = round(1 - share * (count - 1), 4);
  return fractions;
};

const parseNumber = (raw: string) => (raw.trim() === '' ? NaN : Number(raw));

const display = (n: number) => (Number.isFinite(n) ? String(n) : '');

/** Ages are typed, not spun: digits only, so a stray scroll or arrow key never changes them. */
const parseAge = (raw: string) =>
  parseNumber(raw.replace(/\D/g, '').slice(0, 2));

/** A new range starting after the last one, within bounds. */
const nextRange = (
  ranges: AgeRangeValue[],
  bounds: { min: number; max: number },
): AgeRangeValue => {
  const last = ranges[ranges.length - 1];
  const start =
    last && Number.isFinite(last.max)
      ? Math.min(last.max + 1, bounds.max - 1)
      : bounds.min;
  return { min: start, max: Math.min(start + 10, bounds.max), fraction: 0 };
};

const AgeRangesEditor: React.FC<AgeRangesEditorProps> = ({
  ranges,
  onChange,
  bounds,
  maxRanges,
  rowErrors = {},
  error,
  disabled,
}) => {
  const total = ranges.reduce((s, r) => s + (Number(r.fraction) || 0), 0);
  const totalPercent = round(total * 100, 1);
  const balanced = Math.abs(total - 1) <= 0.001;
  // One range is the whole panel, so its weight and the total are implied.
  const single = ranges.length === 1;

  const update = (index: number, patch: Partial<AgeRangeValue>) =>
    onChange(ranges.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const add = () => {
    const next = [...ranges, nextRange(ranges, bounds)];
    const fractions = evenFractions(next.length);
    onChange(next.map((r, i) => ({ ...r, fraction: fractions[i] })));
  };

  const remove = (index: number) => {
    const next = ranges.filter((_, i) => i !== index);
    onChange(next.length === 1 ? [{ ...next[0], fraction: 1 }] : next);
  };

  const distribute = () => {
    const fractions = evenFractions(ranges.length);
    onChange(ranges.map((r, i) => ({ ...r, fraction: fractions[i] })));
  };

  return (
    <Box className={styles.editor}>
      <Box component="ol" className={styles.rows} aria-label="Age ranges">
        {ranges.map((range, index) => {
          const rowError = rowErrors[index];
          const label = `Range ${index + 1}`;
          return (
            <Box component="li" key={index} className={styles.row}>
              <Box className={styles.fields}>
                <TextField
                  label="From age"
                  size="small"
                  value={display(range.min)}
                  disabled={disabled}
                  error={!!rowError}
                  inputProps={{
                    inputMode: 'numeric',
                    'aria-label': `${label} from age`,
                  }}
                  onChange={(e) =>
                    update(index, { min: parseAge(e.target.value) })
                  }
                />
                <span className={styles.dash} aria-hidden>
                  –
                </span>
                <TextField
                  label="To age"
                  size="small"
                  value={display(range.max)}
                  disabled={disabled}
                  error={!!rowError}
                  inputProps={{
                    inputMode: 'numeric',
                    'aria-label': `${label} to age`,
                  }}
                  onChange={(e) =>
                    update(index, { max: parseAge(e.target.value) })
                  }
                />
                {single ? null : (
                  <TextField
                    label="Weight"
                    type="number"
                    size="small"
                    value={display(round(range.fraction * 100, 2))}
                    disabled={disabled}
                    error={!!rowError || !balanced}
                    className={styles.weight}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                    inputProps={{
                      min: 0,
                      max: 100,
                      step: 1,
                      'aria-label': `${label} weight`,
                    }}
                    onChange={(e) =>
                      update(index, {
                        fraction: round(parseNumber(e.target.value) / 100, 4),
                      })
                    }
                  />
                )}
                {!single && (
                  <Tooltip title="Remove range">
                    <span>
                      <IconButton
                        aria-label={`Remove ${label.toLowerCase()}`}
                        onClick={() => remove(index)}
                        disabled={disabled}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
              {rowError && <FormHelperText error>{rowError}</FormHelperText>}
            </Box>
          );
        })}
      </Box>
      <Box className={styles.footer}>
        <Box className={styles.actions}>
          <Button
            startIcon={<AddIcon />}
            onClick={add}
            disabled={disabled || ranges.length >= maxRanges}
          >
            {single ? 'Add another age range' : 'Add range'}
          </Button>
          {ranges.length > 1 && (
            <Button onClick={distribute} disabled={disabled}>
              Split evenly
            </Button>
          )}
        </Box>
        {!single && (
          <Chip
            size="small"
            color={balanced ? 'success' : 'error'}
            variant={balanced ? 'outlined' : 'filled'}
            label={`Total ${totalPercent}%`}
            aria-live="polite"
          />
        )}
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
      {ranges.length >= maxRanges && (
        <FormHelperText>
          Up to {maxRanges} age ranges are supported.
        </FormHelperText>
      )}
    </Box>
  );
};

export default AgeRangesEditor;
